import React, { useEffect, useState } from 'react'
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap'
import useFetch from '../../_fetch'
import { IUser } from './types'

type Preview = {
    eligible: boolean
    errors: string[]
    sourceUsername: string
    newUsername: string
    userId?: number
    dataKey?: string
    incomingLegacySwitches: string[]
    outgoingLegacySwitches: string[]
    skippedSystemAdminTargets: string[]
    warnings: string[]
}

export default function ConvertTrialModal({ show, user, onHide, onConverted }: {
    show: boolean
    user: IUser | null
    onHide: () => void
    onConverted: (previous: IUser, converted: IUser) => void
}) {
    const { doFetch } = useFetch()
    const [newUsername, setNewUsername] = useState('')
    const [preview, setPreview] = useState<Preview | null>(null)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!show) return
        setNewUsername('')
        setPreview(null)
        setError('')
    }, [show, user])

    const requestPreview = async () => {
        if (!user) return
        setLoading(true)
        setError('')
        try {
            const response = await doFetch('/user/admin/trial-conversions/preview', { sourceUsername: user.username, newUsername })
            const body = await response.json().catch(() => null)
            if (!body) throw new Error(await response.text())
            setPreview(body)
            if (!response.ok && !body.errors?.length) throw new Error(body.message || 'Preview failed')
        } catch (value) {
            setError(value instanceof Error ? value.message : 'Preview failed')
        } finally {
            setLoading(false)
        }
    }

    const convert = async () => {
        if (!user || !preview?.eligible) return
        setLoading(true)
        setError('')
        try {
            const response = await doFetch('/user/admin/trial-conversions', { sourceUsername: user.username, newUsername: preview.newUsername })
            const body = await response.json().catch(() => null)
            if (!response.ok) throw new Error(body?.message || 'Conversion failed')
            onConverted(user, body.user)
            onHide()
        } catch (value) {
            setError(value instanceof Error ? value.message : 'Conversion failed')
        } finally {
            setLoading(false)
        }
    }

    return <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Convert trial account</Modal.Title></Modal.Header>
        <Modal.Body>
            <p className="text-muted">The account ID and all stored data remain unchanged. The account becomes e-mail-only and the signup e-mail becomes its manager.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="mb-3" controlId="conversion-username">
                <Form.Label>New account name</Form.Label>
                <Form.Control value={newUsername} onChange={event => { setNewUsername(event.target.value.toLowerCase()); setPreview(null) }} placeholder="supercompany" />
                <Form.Text>2–64 lowercase letters, numbers, dots, underscores or hyphens.</Form.Text>
            </Form.Group>
            {preview && <div data-testid="conversion-preview">
                {preview.errors?.length > 0 && <Alert variant="danger"><ul className="mb-0">{preview.errors.map(item => <li key={item}>{item}</li>)}</ul></Alert>}
                {preview.eligible && <Alert variant="success">Ready to rename <strong>{preview.sourceUsername}</strong> to <strong>{preview.newUsername}</strong>. User ID {preview.userId} and data key {preview.dataKey} will be preserved.</Alert>}
                <dl className="row small">
                    <dt className="col-sm-5">Incoming account switches blocked</dt><dd className="col-sm-7">{preview.incomingLegacySwitches?.join(', ') || 'None'}</dd>
                    <dt className="col-sm-5">Outgoing access imported</dt><dd className="col-sm-7">{preview.outgoingLegacySwitches?.join(', ') || 'None'}</dd>
                    <dt className="col-sm-5">System-admin targets skipped</dt><dd className="col-sm-7">{preview.skippedSystemAdminTargets?.join(', ') || 'None'}</dd>
                </dl>
                {preview.warnings?.map(item => <Alert key={item} variant="warning">{item}</Alert>)}
            </div>}
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={onHide}>Cancel</Button>
            <Button variant="outline-primary" onClick={requestPreview} disabled={loading || !newUsername}>{loading && !preview ? <Spinner size="sm" animation="border" /> : 'Preview'}</Button>
            <Button variant="danger" onClick={convert} disabled={loading || !preview?.eligible}>{loading && preview?.eligible ? 'Converting…' : 'Convert account'}</Button>
        </Modal.Footer>
    </Modal>
}
